// CareDrawer — Tiroir de sélection des soins pour une période (Matin / Midi / Soir)
// Accès rapide aux soins récurrents + catalogue complet par catégories
// Ouverture/fermeture fluide (tiroir), coche animée

import { useState, useMemo } from 'react';
import { CARE_CATEGORIES, CareCategory, RECURRENT_CARES, PeriodInfo } from '../data/careData';

// Palette chaude par période : ambre → orange → rose (dégradé lever → coucher de soleil)
export const PERIOD_COLORS = {
  amber: {
    headerBg: 'bg-gradient-to-r from-amber-50 to-orange-50/50',
    headerBorder: 'border-amber-200/70',
    headerBorderActive: 'border-amber-300',
    text: 'text-amber-800',
    subtext: 'text-amber-600',
    dot: 'bg-amber-500',
    badge: 'bg-amber-500 text-white',
    chip: 'border-amber-200/70 bg-amber-50/60 text-amber-800 hover:border-amber-300 hover:bg-amber-50',
    chipActive: 'border-amber-400 bg-amber-400 text-white',
  },
  orange: {
    headerBg: 'bg-gradient-to-r from-orange-50 to-rose-50/40',
    headerBorder: 'border-orange-200/70',
    headerBorderActive: 'border-orange-300',
    text: 'text-orange-800',
    subtext: 'text-orange-600',
    dot: 'bg-orange-500',
    badge: 'bg-orange-500 text-white',
    chip: 'border-orange-200/70 bg-orange-50/60 text-orange-800 hover:border-orange-300 hover:bg-orange-50',
    chipActive: 'border-orange-400 bg-orange-400 text-white',
  },
  rose: {
    headerBg: 'bg-gradient-to-r from-rose-50 to-pink-50/40',
    headerBorder: 'border-rose-200/70',
    headerBorderActive: 'border-rose-300',
    text: 'text-rose-800',
    subtext: 'text-rose-600',
    dot: 'bg-rose-500',
    badge: 'bg-rose-500 text-white',
    chip: 'border-rose-200/70 bg-rose-50/60 text-rose-800 hover:border-rose-300 hover:bg-rose-50',
    chipActive: 'border-rose-400 bg-rose-400 text-white',
  },
};

interface CareDrawerProps {
  period: PeriodInfo;
  selectedItems: string[];
  onToggleItem: (id: string) => void;
  searchQuery: string;
  defaultOpen?: boolean;
}

export default function CareDrawer({ period, selectedItems, onToggleItem, searchQuery, defaultOpen = false }: CareDrawerProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({});
  const colors = PERIOD_COLORS[period.color];

  const toggleCat = (catId: string) => {
    setExpandedCats((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const count = selectedItems.length;

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return CARE_CATEGORIES;
    const q = searchQuery.toLowerCase();
    return CARE_CATEGORIES.map((cat) => {
      const matchItems = cat.items.filter(
        (item) => !item.isHeader && (item.label.toLowerCase().includes(q) || item.keyword.toLowerCase().includes(q))
      );
      if (matchItems.length > 0) return { ...cat, items: matchItems };
      if (cat.name.toLowerCase().includes(q)) return cat;
      return null;
    }).filter(Boolean) as CareCategory[];
  }, [searchQuery]);

  return (
    <div className="bg-white rounded-xl border border-background-200/60 overflow-hidden">
      {/* En-tête du tiroir */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left cursor-pointer transition-colors ${colors.headerBg} border-b ${isOpen ? colors.headerBorderActive : 'border-transparent'}`}
      >
        <span className="text-xl leading-none w-9 h-9 rounded-xl bg-white/80 border border-background-200/60 flex items-center justify-center flex-shrink-0">
          {period.emoji}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${colors.text}`}>{period.label}</span>
            {count > 0 && (
              <span key={count} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${colors.badge} animate-check-pop`}>
                {count}
              </span>
            )}
          </div>
          <span className={`text-[10px] ${colors.subtext}`}>
            {count > 0 ? `${count} soin${count > 1 ? 's' : ''} sélectionné${count > 1 ? 's' : ''}` : `Aucun soin — ${period.time}`}
          </span>
        </div>
        <i className={`ri-arrow-down-s-line text-foreground-400 text-base transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {/* Corps du tiroir — ouverture fluide via grid-template-rows */}
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden min-h-0">
          <div className="px-3 py-3 space-y-3">
            {/* Soins récurrents — accès rapide */}
            <div>
              <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <i className="ri-flashlight-line text-xs"></i>
                Soins récurrents
              </p>
              <div className="flex flex-wrap gap-1.5">
                {RECURRENT_CARES.map((care) => {
                  const active = selectedItems.includes(care.id);
                  return (
                    <button
                      key={care.id}
                      onClick={() => onToggleItem(care.id)}
                      title={care.hint}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 cursor-pointer whitespace-nowrap ${active ? colors.chipActive : colors.chip}`}
                    >
                      <i className={`${care.icon} text-xs ${active ? 'text-white' : ''}`}></i>
                      {care.label}
                      {active && <i className="ri-check-line text-xs font-bold"></i>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Catalogue complet par catégories */}
            <div className="border-t border-background-100 pt-2 space-y-1.5">
              <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider flex items-center gap-1.5">
                <i className="ri-menu-line text-xs"></i>
                Catalogue complet
              </p>
              {filteredCategories.map((cat) => {
                const isExpanded = expandedCats[cat.id] || !!searchQuery.trim();
                const selectedInCat = cat.items.filter((item) => !item.isHeader && selectedItems.includes(item.id)).length;

                return (
                  <div key={cat.id} className="rounded-lg border border-background-200/60 bg-background-50/50 overflow-hidden">
                    <button
                      onClick={() => toggleCat(cat.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-lg bg-white border border-background-200/60 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-700 flex-1">{cat.name}</span>
                      {selectedInCat > 0 && (
                        <span className="text-[10px] font-bold text-white bg-primary-500 px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {selectedInCat}
                        </span>
                      )}
                      <i className={`ri-arrow-down-s-line text-foreground-400 text-sm transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}></i>
                    </button>

                    {isExpanded && (
                      <div className="divide-y divide-background-100/70 border-t border-background-100/70">
                        {cat.items.map((item) => {
                          if (item.isHeader) {
                            return (
                              <div key={item.id} className="px-3 py-1.5 bg-background-50/70">
                                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{item.label}</span>
                              </div>
                            );
                          }
                          const isSelected = selectedItems.includes(item.id);
                          return (
                            <button
                              key={item.id}
                              onClick={() => onToggleItem(item.id)}
                              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left cursor-pointer transition-colors duration-150 ${isSelected ? 'bg-primary-50/70' : 'hover:bg-background-50'}`}
                            >
                              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-background-300 bg-white'}`}>
                                {isSelected && <i className="ri-check-line text-white text-xs font-bold animate-check-pop"></i>}
                              </div>
                              <span className={`text-sm flex-1 leading-snug ${isSelected ? 'text-foreground-900 font-medium' : 'text-foreground-600'}`}>{item.label}</span>
                              {isSelected && <i className="ri-close-line text-foreground-300 text-sm"></i>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {filteredCategories.length === 0 && (
                <div className="text-center py-6">
                  <i className="ri-search-line text-foreground-300 text-xl block mb-1.5"></i>
                  <p className="text-xs text-foreground-400">Aucun soin trouvé</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}